import React, { CSSProperties, FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { MdClose } from 'react-icons/md';
import { errorText, errorTraceId } from '@srm/core/errors';
import { sendNotification } from '@srm/ui/lib/notifications';
import { IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';
import {
    isJobActive,
    ITrackedJob,
    IVisibleJob,
    jobElapsedMs,
    nextVisibleChangeIn,
    selectVisibleJobs,
} from '@services/backgroundJobs/backgroundJobsReducer';
import {
    BackgroundJobAction,
    backgroundJobKind,
    JOB_ACTION_LABELS,
    primaryJobAction,
} from '@services/backgroundJobs/backgroundJobKinds';
import {
    expectedDurationLabel,
    formatElapsed,
    jobStateLabel,
    progressFraction,
    progressLabel,
    queuePositionLabel,
} from '@services/backgroundJobs/backgroundJobView';
import './BackgroundJobsSection.scss';

/** Сколько строк видно сразу; остальные — по «Ещё N». */
const COLLAPSED_ROWS = 3;

const CANCEL_FAILED_TITLE = 'Фоновую задачу не удалось отменить';

const ROW_SELECTOR = '[data-job-id]';
const LEAVING_CLASS = 'rf-notification-dropdown__job--leaving';
const FOCUSABLE = 'button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';

const findRow = (section: HTMLElement | null, jobId: string): HTMLElement | undefined =>
    [...(section?.querySelectorAll<HTMLElement>(ROW_SELECTOR) ?? [])].find((el) => el.dataset.jobId === jobId);

const isFocusLost = (): boolean => {
    const active = document.activeElement;
    return !active || active === document.body || !active.isConnected;
};

/** Ближайший за `from` фокусируемый элемент области (иначе последний перед ним), кроме отброшенных `skip`. */
const nearestFocusable = (from: Element, scope: Element, skip: (el: Element) => boolean): HTMLElement | null => {
    const candidates = [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !skip(el));
    return (
        candidates.find((el) => from.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) ??
        candidates.at(-1) ??
        null
    );
};

/** Строка уходит или теряет кнопку под фокусом: фокус — в соседнюю строку раздела, иначе — к ближайшему за ним. */
const focusNear = (row: Element, section: HTMLElement): void => {
    const inGoneRow = (el: Element) => row.contains(el) || Boolean(el.closest(`.${LEAVING_CLASS}`));
    const target =
        nearestFocusable(row, section, inGoneRow) ??
        (section.parentElement && nearestFocusable(section, section.parentElement, (el) => section.contains(el)));
    target?.focus();
};

export interface IBackgroundJobsSectionProps {
    jobs: Record<string, ITrackedJob>;
    /** Задачи, об итоге которых уже есть уведомление: закончившаяся уходит из раздела, не дожидаясь конца минуты. */
    notifiedJobIds?: ReadonlySet<string>;
    /** Задачи, чей файл сейчас скачивается: «Скачать» занята. */
    downloadingJobIds?: ReadonlySet<string>;
    /** Фиксированные часы (стори, тесты); без них раздел тикает сам. */
    now?: number;
    /** Раздел под деталью уведомления — вне фокуса и табуляции. */
    inactive?: boolean;
    onCancel: (jobId: string) => Promise<unknown>;
    /** Действие с итогом (реестр видов): скачать файл, открыть документ или страницу результата. */
    onAction: (action: BackgroundJobAction, job: IBackgroundJob) => void;
    /** Скрыть строку итога: задача уходит из раздела и отмечается увиденной. */
    onDismiss?: (jobId: string) => void;
    /** Высота раздела (0 — скрыт): дропдаун добавляет её к высоте ленты, а не отнимает у неё. */
    onHeightChange?: (height: number) => void;
}

/**
 * Часы раздела: раз в секунду, пока есть идущая задача (секундомер), иначе — один раз к ближайшей смене состава
 * (у итога вышло окно показа). Раздел смонтирован только при открытом дропдауне — закрытый не тикает.
 */
const useSectionClock = (jobs: Record<string, ITrackedJob>, fixedNow: number | undefined): number => {
    const [now, setNow] = useState(() => fixedNow ?? Date.now());
    const hasActive = useMemo(() => Object.values(jobs).some((tracked) => isJobActive(tracked.job)), [jobs]);

    // Новый снимок задач — свежие часы: иначе итог, пришедший после долгой паузы, считался бы по старому «сейчас».
    useEffect(() => {
        if (fixedNow === undefined) setNow(Date.now());
    }, [fixedNow, jobs]);

    useEffect(() => {
        if (fixedNow !== undefined) return undefined;
        const delay = hasActive ? 1000 : nextVisibleChangeIn(jobs, now);
        if (delay === null) return undefined;
        const timer = window.setTimeout(() => setNow(Date.now()), delay);
        return () => window.clearTimeout(timer);
    }, [fixedNow, hasActive, jobs, now]);

    return fixedNow ?? now;
};

const JobMark: FC<{ job: IBackgroundJob }> = ({ job }) => {
    const finished = !isJobActive(job);
    return (
        <span className="rf-notification-dropdown__job-mark" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28">
                <circle className="rf-notification-dropdown__job-track" cx="14" cy="14" r="11" />
                <circle className="rf-notification-dropdown__job-arc" cx="14" cy="14" r="11" />
                <path className="rf-notification-dropdown__job-icon" d={backgroundJobKind(job.kind).iconPath} />
                {finished && job.status === 'succeeded' && (
                    <path className="rf-notification-dropdown__job-done" d="m9.5 14.3 3 3 6-6.3" />
                )}
                {finished && job.status === 'failed' && (
                    <path className="rf-notification-dropdown__job-done" d="M14 9.5v5.5M14 18.4v.1" />
                )}
                {finished && job.status === 'cancelled' && (
                    <path className="rf-notification-dropdown__job-done" d="M10 14h8" />
                )}
            </svg>
        </span>
    );
};

/**
 * Подпись кнопки в узкой строке: длинная съела бы название задачи. Ссылку подписывает настройка кнопки («Открыть
 * инвестиционные проекты») — видно «Открыть»; уточнение файла («…результата», «…с ошибками») — «Скачать файл».
 * Полная подпись — в имени кнопки и подсказке.
 */
const rowActionText = (action: BackgroundJobAction): string => {
    if (action.type === 'openLink') return JOB_ACTION_LABELS.openLink;
    if (action.type === 'download' && action.label.startsWith(JOB_ACTION_LABELS.download)) {
        return JOB_ACTION_LABELS.download;
    }
    return action.label;
};

const joinMeta = (parts: (string | null)[]): string | null => parts.filter(Boolean).join(' · ') || null;

const jobMeta = (item: IVisibleJob, now: number): string | null => {
    const { job } = item;
    if (job.status === 'queued')
        return joinMeta([queuePositionLabel(job.queuePosition), expectedDurationLabel(job.expectedMs)]);
    if (job.status === 'failed') return job.error ?? null;
    if (job.status === 'cancelled') return null;
    if (job.status === 'succeeded' && job.summary) return job.summary;
    const elapsed = jobElapsedMs(item, now);
    const active = isJobActive(job);
    return joinMeta([
        job.status === 'running' ? progressLabel(job.kind, job.progress) : null,
        elapsed === null ? null : formatElapsed(elapsed),
        active ? expectedDurationLabel(job.expectedMs) : null,
    ]);
};

/** Длина окружности дуги (r=11), как в `services/backgroundJobs/_jobRing.scss`. */
const ARC_LENGTH = 69.2;

/** Идущая задача с известным общим числом — определённая дуга вместо вращения. */
const determinateArc = (job: IBackgroundJob): CSSProperties | undefined => {
    const fraction = job.status === 'running' ? progressFraction(job.progress) : null;
    if (fraction === null) return undefined;
    return { '--nd-job-dash': (fraction * ARC_LENGTH).toFixed(1) } as CSSProperties;
};

interface IJobRowProps {
    item: IVisibleJob;
    now: number;
    cancelling: boolean;
    downloading: boolean;
    onCancel: (jobId: string) => void;
    onAction: (action: BackgroundJobAction, job: IBackgroundJob) => void;
    onDismiss?: (jobId: string, row: Element | null) => void;
}

const JobRow: FC<IJobRowProps> = ({ item, now, cancelling, downloading, onCancel, onAction, onDismiss }) => {
    const { job } = item;
    const kind = backgroundJobKind(job.kind);
    const state = jobStateLabel(job);
    const meta = jobMeta(item, now);
    const action = primaryJobAction(job);
    const canCancel = job.status === 'queued' || job.status === 'running';
    const title = job.title || kind.defaultTitle;
    const subtitle = kind.subtitle(job);
    const arc = determinateArc(job);
    const canDismiss = onDismiss !== undefined && !isJobActive(job);
    const busy = downloading && action?.type === 'download';
    const actionText = action && rowActionText(action);

    return (
        <div
            role="listitem"
            className={clsx(
                'rf-notification-dropdown__job',
                `rf-notification-dropdown__job--${job.status}`,
                item.leaving && 'rf-notification-dropdown__job--leaving',
                arc && 'rf-notification-dropdown__job--determinate',
            )}
            style={arc}
            data-job-id={job.jobId}
        >
            <JobMark job={job} />
            <span className="rf-notification-dropdown__job-body">
                <span className="rf-notification-dropdown__job-title" title={title}>
                    {title}
                </span>
                {subtitle && (
                    <span className="rf-notification-dropdown__job-file" title={subtitle}>
                        {subtitle}
                    </span>
                )}
                <span className="rf-notification-dropdown__job-sub" title={meta ? `${state} · ${meta}` : undefined}>
                    <b className="rf-notification-dropdown__job-state">{state}</b>
                    {meta && <> · {meta}</>}
                </span>
            </span>
            <span className="rf-notification-dropdown__job-acts">
                {canCancel && (
                    <button
                        type="button"
                        className="rf-notification-dropdown__job-act rf-notification-dropdown__job-act--ghost"
                        aria-label={`Отменить: ${title}`}
                        disabled={cancelling}
                        onClick={() => onCancel(job.jobId)}
                    >
                        Отменить
                    </button>
                )}
                {action && (
                    <button
                        type="button"
                        className="rf-notification-dropdown__job-act"
                        aria-label={`${action.label}: ${title}`}
                        title={actionText === action.label ? undefined : action.label}
                        aria-busy={busy || undefined}
                        aria-disabled={busy || undefined}
                        onClick={() => !busy && onAction(action, job)}
                    >
                        {actionText}
                    </button>
                )}
                {canDismiss && (
                    <button
                        type="button"
                        className="rf-notification-dropdown__job-dismiss"
                        aria-label={`Скрыть: ${title}`}
                        title="Скрыть задачу"
                        tabIndex={item.leaving ? -1 : undefined}
                        onClick={(event) => onDismiss(job.jobId, event.currentTarget.closest(ROW_SELECTOR))}
                    >
                        <MdClose size={15} />
                    </button>
                )}
            </span>
        </div>
    );
};

/**
 * Раздел «Фоновые задачи» над лентой колокольчика: идущие задачи с этапом и временем и итоги последней минуты.
 * Нечего показывать — раздела нет.
 */
const BackgroundJobsSection: FC<IBackgroundJobsSectionProps> = ({
    jobs,
    notifiedJobIds,
    downloadingJobIds,
    now: fixedNow,
    inactive,
    onCancel,
    onAction,
    onDismiss,
    onHeightChange,
}) => {
    const now = useSectionClock(jobs, fixedNow);
    const visible = useMemo(
        () =>
            selectVisibleJobs(jobs, now).filter(
                (item) => isJobActive(item.job) || !notifiedJobIds?.has(item.job.jobId),
            ),
        [jobs, now, notifiedJobIds],
    );
    const [expanded, setExpanded] = useState(false);
    const [cancelling, setCancelling] = useState<ReadonlySet<string>>(new Set());
    const sectionRef = useRef<HTMLElement | null>(null);
    /** Задача, с кнопки отмены которой ушёл фокус: когда отмена закончится, фокус вернётся в её строку. */
    const refocusJobRef = useRef<string | null>(null);
    /** Кнопка строки под фокусом: задача может закончиться, пока он на «Отменить», и кнопка уйдёт из-под него. */
    const focusedRef = useRef<{ jobId: string; element: HTMLElement } | null>(null);
    const onHeightChangeRef = useRef(onHeightChange);
    onHeightChangeRef.current = onHeightChange;

    const hidden = visible.length === 0;
    const shown = expanded ? visible : visible.slice(0, COLLAPSED_ROWS);
    const rest = visible.length - shown.length;
    const activeCount = visible.filter((item) => isJobActive(item.job)).length;

    useLayoutEffect(() => {
        const section = sectionRef.current;
        if (!section) {
            onHeightChangeRef.current?.(0);
            return undefined;
        }
        onHeightChangeRef.current?.(section.offsetHeight);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => onHeightChangeRef.current?.(section.offsetHeight));
        observer.observe(section);
        return () => observer.disconnect();
    }, [hidden]);

    useEffect(() => () => onHeightChangeRef.current?.(0), []);

    useEffect(() => {
        if (visible.length <= COLLAPSED_ROWS) setExpanded(false);
    }, [visible.length]);

    useLayoutEffect(() => {
        const jobId = refocusJobRef.current;
        const section = sectionRef.current;
        if (jobId === null || cancelling.has(jobId)) return;
        refocusJobRef.current = null;
        if (!section || !isFocusLost()) return;
        const row = findRow(section, jobId);
        const own = row?.querySelector<HTMLElement>(FOCUSABLE);
        if (own) own.focus();
        else if (row) focusNear(row, section);
    });

    useLayoutEffect(() => {
        const focused = focusedRef.current;
        const section = sectionRef.current;
        if (!focused || focused.element.isConnected) return;
        focusedRef.current = null;
        if (!section || !isFocusLost()) return;
        const row = findRow(section, focused.jobId);
        const own = row?.querySelector<HTMLElement>(FOCUSABLE);
        if (own) own.focus();
        else if (row) focusNear(row, section);
    });

    const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
        const jobId = event.target.closest<HTMLElement>(ROW_SELECTOR)?.dataset.jobId;
        focusedRef.current = jobId ? { jobId, element: event.target } : null;
    };

    const handleCancel = (jobId: string) => {
        if (findRow(sectionRef.current, jobId)?.contains(document.activeElement)) refocusJobRef.current = jobId;
        setCancelling((prev) => new Set(prev).add(jobId));
        onCancel(jobId)
            .catch((error: unknown) =>
                sendNotification({
                    title: CANCEL_FAILED_TITLE,
                    message: errorText(error),
                    variant: 'red',
                    traceId: errorTraceId(error),
                }),
            )
            .finally(() =>
                setCancelling((prev) => {
                    const next = new Set(prev);
                    next.delete(jobId);
                    return next;
                }),
            );
    };

    const handleDismiss = (jobId: string, row: Element | null) => {
        const section = sectionRef.current;
        if (row && section && row.contains(document.activeElement)) focusNear(row, section);
        onDismiss?.(jobId);
    };

    if (hidden) return null;

    return (
        <section
            ref={sectionRef}
            className="rf-notification-dropdown__jobs"
            aria-label="Фоновые задачи"
            inert={inactive || undefined}
            onFocus={handleFocus}
        >
            <div className="rf-notification-dropdown__group rf-notification-dropdown__jobs-head">
                <span className="rf-notification-dropdown__group-label">Фоновые задачи</span>
                <span
                    className="rf-notification-dropdown__group-count rf-notification-dropdown__group-count--job"
                    title={activeCount > 0 ? `Идёт: ${activeCount}` : undefined}
                >
                    {visible.length}
                </span>
                <span className="rf-notification-dropdown__group-rule" aria-hidden="true" />
            </div>
            <div role="list" className="rf-notification-dropdown__jobs-list">
                {shown.map((item) => (
                    <JobRow
                        key={item.job.jobId}
                        item={item}
                        now={now}
                        cancelling={cancelling.has(item.job.jobId)}
                        downloading={downloadingJobIds?.has(item.job.jobId) ?? false}
                        onCancel={handleCancel}
                        onAction={onAction}
                        onDismiss={onDismiss && handleDismiss}
                    />
                ))}
            </div>
            {(rest > 0 || expanded) && (
                <button
                    type="button"
                    className="rf-notification-dropdown__jobs-more"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((value) => !value)}
                >
                    {expanded ? 'Свернуть' : `Ещё ${rest}`}
                </button>
            )}
        </section>
    );
};

export default BackgroundJobsSection;
